// frontend/src/components/auth/LoginForm.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useAuth } from './AuthProvider';
import AuthLayout from './AuthLayout';

export const LoginForm = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const { login } = useAuth();
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username) {
      newErrors.username = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.username)) {
      newErrors.username = 'Email is invalid';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await login(formData);
      navigate('/');
    } catch (error) {
      // Error handled by auth context
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to your account">
      <form className="space-y-5" onSubmit={handleSubmit}>
        {/* Email */}
        <div className="space-y-1.5">
          <label htmlFor="username" className="block text-small font-semibold text-ink-soft">
            Email address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-ink-muted" />
            </div>
            <input
              id="username"
              name="username"
              type="email"
              autoComplete="email"
              required
              value={formData.username}
              onChange={handleChange}
              className={`w-full min-h-touch pl-10 pr-3 rounded-lg border text-body
                focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent
                bg-white text-ink-soft placeholder-ink-muted
                ${errors.username ? 'border-danger' : 'border-border-strong'}`}
              placeholder="Enter your email"
            />
          </div>
          {errors.username && (
            <p className="text-small text-danger flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.username}
            </p>
          )}
        </div>

        {/* Password */}
        <div className="space-y-1.5">
          <label htmlFor="password" className="block text-small font-semibold text-ink-soft">
            Password
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-ink-muted" />
            </div>
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={formData.password}
              onChange={handleChange}
              className={`w-full min-h-touch pl-10 pr-10 rounded-lg border text-body
                focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent
                bg-white text-ink-soft placeholder-ink-muted
                ${errors.password ? 'border-danger' : 'border-border-strong'}`}
              placeholder="Enter your password"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center min-w-touch justify-center"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword
                ? <EyeOff className="h-5 w-5 text-ink-muted" />
                : <Eye className="h-5 w-5 text-ink-muted" />}
            </button>
          </div>
          {errors.password && (
            <p className="text-small text-danger flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.password}
            </p>
          )}
        </div>

        {/* Remember + Forgot */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              className="h-4 w-4 text-accent focus:ring-accent border-border-strong rounded"
            />
            <label htmlFor="remember-me" className="ml-2 text-body text-ink-soft">
              Remember me
            </label>
          </div>
          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="text-body text-accent hover:text-accent-hover transition-colors"
          >
            Forgot password?
          </button>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full min-h-touch flex items-center justify-center rounded-lg
            bg-accent text-white font-medium text-body
            hover:bg-accent-hover transition-colors
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          ) : (
            'Sign in'
          )}
        </button>

        {/* Register link */}
        <p className="text-center text-body text-ink-muted">
          Don't have an account?{' '}
          <button
            type="button"
            onClick={() => navigate('/register')}
            className="text-accent hover:text-accent-hover font-medium transition-colors"
          >
            Sign up
          </button>
        </p>
      </form>
    </AuthLayout>
  );
};
